import { useCallback } from 'react';
import {
  Button,
  EllipsisMenuButton,
  Flex,
  Menu,
  MenuContent,
  MenuTrigger,
  toaster,
} from '@optiaxiom/react';
import { IconClockRotateLeft, IconCopy, IconPlay, IconTrashCan } from '@optimizely/axiom-icons';

import { useInstanceId, useNamespacedNavigate } from '@frontend/hooks';

import { exportSpecializedAgent } from '../../services/specialized-agent';
import { exportWorkflowAgent } from '../../services/workflow-agent';

type AgentActionButtonsProps = {
  type: 'specialized' | 'workflow';
  agentId?: string;
  isTestRunDisabled: boolean;
  isEditing: boolean;
  isSaveDisabled: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  onSave: () => void;
  onViewHistory?: () => void;
  isSidebarOpen?: boolean;
  onTestRun: () => void;
  onDelete: () => void;
  isEmbedded?: boolean;
};

export function AgentActionButtons({
  type,
  agentId,
  isTestRunDisabled,
  isEditing,
  isSaveDisabled,
  isCreating,
  isUpdating,
  isSidebarOpen,
  onSave,
  onViewHistory,
  onTestRun,
  onDelete,
  isEmbedded,
}: AgentActionButtonsProps) {
  const navigate = useNamespacedNavigate();
  const { instanceId } = useInstanceId();

  const handleCopyAgent = useCallback(() => {
    if (agentId) {
      navigate(`/agents/${type}/${agentId}/copy`);
    }
  }, [agentId, navigate, type]);

  const handleExportAgent = useCallback(async () => {
    if (!instanceId || !agentId) return;

    try {
      const data =
        type === 'specialized'
          ? await exportSpecializedAgent(instanceId, agentId)()
          : await exportWorkflowAgent(instanceId, agentId);
      if (data) {
        const jsonString = JSON.stringify(data, null, 2);
        await navigator.clipboard.writeText(jsonString);
        toaster.create('Agent copied to clipboard as JSON', { intent: 'success' });
      }
    } catch {
      toaster.create('Failed to export agent. Please try again.', { intent: 'danger' });
    }
  }, [instanceId, agentId, type]);

  const extraOptions = (
    <Menu
      options={[
        {
          label: 'Duplicate Agent',
          addon: <IconCopy />,
          intent: 'neutral',
          execute: handleCopyAgent,
        },
        {
          label: 'Copy as JSON',
          addon: <IconCopy />,
          intent: 'neutral',
          execute: handleExportAgent,
        },
        ...(onViewHistory
          ? [
              {
                label: 'View Version History',
                addon: <IconClockRotateLeft />,
                intent: 'neutral' as const,
                execute: onViewHistory,
              },
            ]
          : []),
        {
          label: 'Delete Agent',
          addon: <IconTrashCan />,
          intent: 'danger',
          group: {
            label: 'Delete Agent',
            hidden: true,
            separator: true,
          },
          execute: onDelete,
        },
      ]}
    >
      <MenuTrigger asChild>
        <EllipsisMenuButton aria-label="Agent actions menu" />
      </MenuTrigger>
      <MenuContent />
    </Menu>
  );

  const saveButton = (
    <Button
      appearance="primary"
      onClick={onSave}
      loading={isCreating || isUpdating}
      disabled={isSaveDisabled}
    >
      {isEditing ? 'Update' : 'Save'}
    </Button>
  );

  const testRunButton = (
    <Button
      icon={<IconPlay />}
      appearance="default"
      disabled={isTestRunDisabled}
      onClick={onTestRun}
    >
      Test Run
    </Button>
  );

  return (
    <Flex gap="16" flexDirection="row">
      {isSidebarOpen ? saveButton : agentId && extraOptions}
      {isEmbedded ? null : testRunButton}
      {isSidebarOpen ? agentId && extraOptions : saveButton}
    </Flex>
  );
}
